import { ChangeEvent, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { TableDropdown } from "components/dropdowns/TableDropdown"
import { TablePagination } from "components/paginations/TablePagination"
import { SearchInput } from "components/search/SearchInput"
import { Column, DataTable } from "components/tables/DataTable"

import { BrandSiteProvider } from "features/cms-seo/BrandSiteContext"
import { GameSeoModal } from "features/cms-seo/GameSeoModal"

import { gameService } from "services/game/game.service"
import { GameInterface } from "services/game/type"

import { GameDescriptionModal } from "../GameDescriptionModal"
import { GameImageOverrideModal } from "../GameImageOverrideModal"

const PAGE_SIZE_CHOICES = [25, 50, 100]
const DEFAULT_PAGE_SIZE = PAGE_SIZE_CHOICES[1]

type Translate = (key: string, options?: Record<string, unknown>) => string

export const LibraryTab = () => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [games, setGames] = useState<GameInterface[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState<string>("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState(1)
  const [refreshKey, setRefreshKey] = useState(0)
  const [imageModalGame, setImageModalGame] = useState<GameInterface | null>(null)
  const [descriptionModalGame, setDescriptionModalGame] = useState<GameInterface | null>(null)
  const [seoModalGame, setSeoModalGame] = useState<GameInterface | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const data = await gameService.getAllGames({
        page,
        pageSize,
        search,
        category: category || undefined,
        includeDisabled: true
      })
      setLoading(false)
      if (!data) return
      setGames((data.games ?? []) as GameInterface[])
      setTotalPages(data.total_pages ?? 1)
    }
    load()
  }, [page, pageSize, search, category, refreshKey])

  const onToggle = async (row: GameInterface) => {
    const next = !row.enabled
    setGames((prev) => prev.map((g) => (g.identifier === row.identifier ? { ...g, enabled: next } : g)))
    const affected = await gameService.toggleEnabled(row.identifier, next)
    if (affected !== null) {
      const message = String(
        next
          ? t("gameManagement.library.toggledEnabled", { defaultValue: "{{title}} enabled", title: row.title })
          : t("gameManagement.library.toggledDisabled", { defaultValue: "{{title}} disabled", title: row.title })
      )
      const suffix =
        affected > 1
          ? ` ${String(
              t("gameManagement.library.toggledDuplicates", {
                defaultValue: "({{count}} duplicate rows)",
                count: affected
              })
            )}`
          : ""
      toast.success(`${message}${suffix}`)
    } else {
      setGames((prev) => prev.map((g) => (g.identifier === row.identifier ? { ...g, enabled: !next } : g)))
    }
  }

  const onImageOverrideChanged = () => {
    setImageModalGame(null)
    setRefreshKey((k) => k + 1)
  }

  const onDescriptionSaved = (savedDescription: string | null) => {
    if (descriptionModalGame) {
      const targetId = descriptionModalGame.identifier
      setGames((prev) => prev.map((g) => (g.identifier === targetId ? { ...g, description: savedDescription ?? "" } : g)))
    }
    setDescriptionModalGame(null)
  }

  const categoryOptions: { value: string; label: string }[] = [
    { value: "", label: t("gameManagement.library.allCategories", { defaultValue: "All categories" }) },
    { value: "slots", label: t("gameManagement.library.catSlots", { defaultValue: "Slots" }) },
    { value: "live", label: t("gameManagement.library.catLive", { defaultValue: "Live" }) },
    { value: "new", label: t("gameManagement.library.catNew", { defaultValue: "New" }) },
    { value: "shows", label: t("gameManagement.library.catShows", { defaultValue: "Shows" }) }
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <div className="w-full md:w-60">
          <SearchInput
            placeholder={t("gameManagement.library.searchPlaceholder", { defaultValue: "Search games…" })}
            onChange={setSearch}
          />
        </div>

        <select
          className="select select-bordered w-full md:w-48 bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
          value={category}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setCategory(e.target.value)
            setPage(1)
          }}
        >
          {categoryOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        <div className="md:ml-auto">
          <TableDropdown
            value={pageSize}
            onValueChange={(v: number) => {
              setPageSize(v)
              setPage(1)
            }}
            columns={PAGE_SIZE_CHOICES}
          />
        </div>
      </div>

      {loading ? (
        <p className="py-8 text-center text-lg text-[rgba(6,7,21,1)] dark:text-white">
          {t("gameManagement.common.loading", { defaultValue: "Loading…" })}
        </p>
      ) : games.length === 0 ? (
        <p className="py-8 text-center text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">
          {t("gameManagement.library.empty", { defaultValue: "No games found." })}
        </p>
      ) : (
        <DataTable
          rows={games}
          rowKey={(g) => g.identifier}
          columns={libraryColumns({
            t,
            onOpenImage: setImageModalGame,
            onOpenDescription: setDescriptionModalGame,
            onOpenSeo: setSeoModalGame,
            onToggle
          })}
        />
      )}

      <TablePagination page={page} totalPages={totalPages} onJump={setPage} />

      <GameImageOverrideModal
        open={!!imageModalGame}
        game={imageModalGame}
        onClose={() => setImageModalGame(null)}
        onSaved={onImageOverrideChanged}
      />

      <GameDescriptionModal
        open={!!descriptionModalGame}
        game={descriptionModalGame}
        onClose={() => setDescriptionModalGame(null)}
        onSaved={onDescriptionSaved}
      />

      {seoModalGame && (
        <BrandSiteProvider>
          <GameSeoModal
            game={seoModalGame}
            onClose={() => setSeoModalGame(null)}
            onSaved={() => {
              setSeoModalGame(null)
              setRefreshKey((k) => k + 1)
            }}
          />
        </BrandSiteProvider>
      )}
    </div>
  )
}

const libraryColumns = ({
  t,
  onOpenImage,
  onOpenDescription,
  onOpenSeo,
  onToggle
}: {
  t: Translate
  onOpenImage: (g: GameInterface) => void
  onOpenDescription: (g: GameInterface) => void
  onOpenSeo: (g: GameInterface) => void
  onToggle: (g: GameInterface) => void
}): Column<GameInterface>[] => [
  {
    header: t("gameManagement.library.col.id", { defaultValue: "ID" }),
    render: (g) => <span className="font-mono text-xs">{g.identifier}</span>
  },
  {
    header: t("gameManagement.library.col.image", { defaultValue: "Image" }),
    render: (g) => (
      <button
        type="button"
        onClick={() => onOpenImage(g)}
        className="relative group block cursor-pointer"
        title={
          g.image_vertical_override
            ? t("gameManagement.library.imageOverrideTip", { defaultValue: "Custom override — click to edit or reset" })
            : t("gameManagement.library.imageUploadTip", { defaultValue: "Click to upload a custom image" })
        }
      >
        {g.image_vertical || g.image ? (
          <img src={g.image_vertical || g.image} alt={g.title} className="h-16 w-auto rounded transition-opacity group-hover:opacity-70" />
        ) : (
          <div className="h-16 w-12 rounded bg-black/[0.05] dark:bg-white/[0.05] flex items-center justify-center text-[10px] opacity-50">
            {t("gameManagement.library.noImage", { defaultValue: "no image" })}
          </div>
        )}
        {g.image_vertical_override && (
          <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-[rgba(132,_0,_168,_1)] text-white text-[10px] font-medium pointer-events-none">
            {t("gameManagement.library.customBadge", { defaultValue: "custom" })}
          </span>
        )}
      </button>
    )
  },
  { header: t("gameManagement.library.col.title", { defaultValue: "Title" }), render: (g) => g.title },
  { header: t("gameManagement.library.col.provider", { defaultValue: "Provider" }), render: (g) => g.gameProvider?.title ?? g.provider },
  { header: t("gameManagement.library.col.category", { defaultValue: "Category" }), render: (g) => g.category },

  {
    header: t("gameManagement.library.col.description", { defaultValue: "Description" }),
    render: (g) => {
      const hasDescription = typeof g.description === "string" && g.description.trim().length > 0
      return (
        <button
          type="button"
          onClick={() => onOpenDescription(g)}
          className={clsx(
            "btn btn-xs",
            hasDescription
              ? "bg-[rgba(132,_0,_168,_0.12)] text-[rgba(132,_0,_168,_1)] dark:text-[rgba(217,_140,_237,_1)] border-[rgba(132,_0,_168,_0.4)] hover:bg-[rgba(132,_0,_168,_0.2)]"
              : "btn-ghost border border-black/10 dark:border-white/10"
          )}
          title={
            hasDescription
              ? t("gameManagement.library.descriptionEditTip", { defaultValue: "Edit description" })
              : t("gameManagement.library.descriptionAddTip", { defaultValue: "Add description" })
          }
        >
          {hasDescription
            ? t("gameManagement.library.descriptionEdit", { defaultValue: "Edit" })
            : t("gameManagement.library.descriptionAdd", { defaultValue: "+ Add" })}
        </button>
      )
    }
  },
  {
    header: t("gameManagement.library.col.seo", { defaultValue: "SEO" }),
    render: (g) => {
      const hasSeo = !!g.seo && Object.keys(g.seo).length > 0
      return (
        <button
          type="button"
          onClick={() => onOpenSeo(g)}
          className={clsx(
            "btn btn-xs",
            hasSeo
              ? "bg-[rgba(132,_0,_168,_0.12)] text-[rgba(132,_0,_168,_1)] dark:text-[rgba(217,_140,_237,_1)] border-[rgba(132,_0,_168,_0.4)] hover:bg-[rgba(132,_0,_168,_0.2)]"
              : "btn-ghost border border-black/10 dark:border-white/10"
          )}
          title={
            hasSeo
              ? t("gameManagement.library.seoEditTip", { defaultValue: "Edit search metadata" })
              : t("gameManagement.library.seoAddTip", { defaultValue: "Add search metadata" })
          }
        >
          {hasSeo
            ? t("gameManagement.library.seoEdit", { defaultValue: "Edit SEO" })
            : t("gameManagement.library.seoAdd", { defaultValue: "+ Add SEO" })}
        </button>
      )
    }
  },
  {
    header: t("gameManagement.library.col.enabled", { defaultValue: "Enabled" }),
    align: "right",
    render: (g) => <input type="checkbox" className={clsx("toggle")} checked={g.enabled} onChange={() => onToggle(g)} />
  }
]
